import { useState } from 'react';
import { Thread } from './chat/Thread';
import { useCustomLangGraphRuntime } from '../services/langGraphRuntime';
import { shortid } from '../services/id.service';
import { ConversationHistory } from './chat/ConversationHistory';

interface RuthieProps {
  initialQuestion?: string;
  showHistory?: boolean;
  conversationId?: string;
}

// Wrapper component that renders the assistant with a specific thread ID
const AssistantWrapper = ({
  threadId,
  initialQuestion,
  onPlanExecuted,
}: {
  threadId: string;
  initialQuestion: string;
  onPlanExecuted: (result: unknown) => void;
}) => {
  const { runtime, isHistoricalThread, isLoaded, resumeGraph } = useCustomLangGraphRuntime({
    agentType: 'data_architect',
    model: 'claude-opus-4-6',
    threadId,
  });

  // Auto-run for new threads only
  // Historical threads should start collapsed to avoid loading all at once
  const autoRunEnabled = !isHistoricalThread;

  // Show loading state while loading historical thread
  if (isHistoricalThread && !isLoaded) {
    return (
      <div className="p-4">
        <p>Loading conversation...</p>
      </div>
    );
  }

  return (
    <Thread
      runtime={runtime}
      initialQuestion={initialQuestion}
      toolCallHandlers={{
        onPlanExecuted,
        onResumeGraph: resumeGraph,
      }}
      autoRunCharts={autoRunEnabled}
      emptyStateMessage="Hi, I'm Ruthie! I can help you build and modify your semantic layer in Pliable's develop mode. Need to create metrics, define relationships, or set up dimensions? Just ask!"
    />
  );
};

export const Ruthie: React.FC<RuthieProps> = ({
  initialQuestion = '',
  showHistory = false,
  conversationId,
}) => {
  // Use conversationId if provided, otherwise generate a new thread ID
  const [defaultThreadId, setDefaultThreadId] = useState(() => shortid());
  const currentThreadId = conversationId || defaultThreadId;

  const [executedPlans, setExecutedPlans] = useState<string[]>([]);

  const handleNewChat = () => {
    setDefaultThreadId(shortid());
    setExecutedPlans([]);
  };

  const content = (
    <AssistantWrapper
      key={currentThreadId}
      threadId={currentThreadId}
      initialQuestion={initialQuestion}
      onPlanExecuted={(result) => {
        // Track executed plans if needed
        if (result && typeof result === 'object' && 'success' in result) {
          const planResult = result as { success: boolean };
          if (planResult.success) {
            setExecutedPlans([...executedPlans, currentThreadId]);
          }
        }
      }}
    />
  );

  if (showHistory) {
    return (
      <div className="h-full w-full flex">
        <div className="flex-[3] min-w-0">
          {content}
        </div>
        <div className="flex-1 min-w-0">
          <ConversationHistory
            activeThreadId={currentThreadId}
            onNewChat={handleNewChat}
            agentType="data_architect"
            baseUrl="/ruthie"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      {content}
    </div>
  );
};
